export const courseMeta = {
  module: 'Module 2 · Opto-Acoustic Techniques for Dimensional Monitoring & Process Assessment',
  title: 'AI for Experimental Data Analysis in Mechanical Systems Monitoring',
  titleEmphasis: 'Experimental Data',
  lede:
    'Concepts, methods, and hands-on practice for applying machine learning and deep learning to experimental data in mechanics — curves, signals, fields, and images.',
  institution: 'Politecnico di Bari',
  programme: 'MSc Mechanical Engineering',
  duration: '5 hours',
  language: 'English',
  instructor: 'Dr. D. Katamba Mpoyi',
};

export const learningContext =
  'This module is the second part of Opto-Acoustic Techniques for Dimensional Monitoring & Process Assessment. The opto-acoustic, dimensional, and process measurements introduced in Part 1 are exactly the kind of experimental data these AI methods are built to analyze.';

export const outcomes = [
  'Understand the main opportunities of AI for experimental mechanics.',
  'Prepare and visualize a simple experimental dataset (curves, signals, fields, images).',
  'Apply clustering to identify groups of similar experiments.',
  'Train a simple machine-learning model to predict a mechanical property.',
  'Understand where deep learning is useful for images, signals, and experimental curves.',
  'Interpret AI results from a mechanical and physical point of view.',
];

export const schedule = [
  { id: '01', title: 'Introduction — AI & experimental mechanics', duration: '30 min', isLab: false },
  { id: '02', title: 'Experimental data in mechanics', duration: '45 min', isLab: false },
  { id: 'LAB 1', title: 'Getting started with experimental data', duration: '45 min', isLab: true },
  { id: '03', title: 'Introduction to machine learning', duration: '40 min', isLab: false },
  { id: 'LAB 2', title: 'Clustering experimental tests', duration: '50 min', isLab: true },
  { id: '04', title: 'Supervised learning for mechanical property prediction', duration: '40 min', isLab: false },
  { id: 'LAB 3', title: 'Predicting a mechanical property', duration: '50 min', isLab: true },
  { id: '05', title: 'Introduction to deep learning', duration: '35 min', isLab: false },
  { id: 'LAB 4', title: 'Mini deep-learning demonstration', duration: '35 min', isLab: true },
  { id: '06', title: 'Opportunities, limitations & good practices', duration: '25 min', isLab: false },
  { id: '07', title: 'Conclusion & discussion', duration: '10 min', isLab: false },
];

export type ProgramTopic = { heading: string; items: string[] };

export type ProgramBlock = {
  id: string;
  title: string;
  duration: string;
  type: 'lecture' | 'lab';
  summary?: string;
  topics?: ProgramTopic[];
  items?: string[];
  tools?: string;
  discussion?: string;
  paper?: string;
  options?: string[];
};

export const programBlocks: ProgramBlock[] = [
  {
    id: '01',
    title: 'Introduction — AI & Experimental Mechanics',
    duration: '30 min',
    type: 'lecture',
    topics: [
      {
        heading: 'Why AI in experimental mechanics?',
        items: [
          'Increasing volume of experimental data.',
          'Data from mechanical tests, sensors, cameras, DIC, vibration, fatigue and fracture tests — including opto-acoustic and dimensional measurements from Part 1.',
          'Limits of classical analysis when datasets become large or complex.',
        ],
      },
      {
        heading: 'What AI can bring',
        items: [
          'Automatic classification of experiments.',
          'Anomaly detection.',
          'Prediction of mechanical properties.',
          'Experimental image analysis; decision support for test campaigns.',
        ],
      },
    ],
    paper: 'Real examples — bolt-loosening detection (vibration + DL, P1) and tool-wear monitoring in milling (deep learning + data fusion, P4).',
  },
  {
    id: '02',
    title: 'Experimental Data in Mechanics',
    duration: '45 min',
    type: 'lecture',
    topics: [
      {
        heading: 'Types of experimental data',
        items: [
          'Force–displacement and stress–strain curves.',
          'Vibration signals.',
          'Crack or damage images.',
          'Displacement and strain fields (DIC).',
          'Multi-sensor data.',
        ],
      },
      {
        heading: 'Common challenges',
        items: [
          'Noise, missing data, outliers, different scales between variables.',
          'Variability between tests; limited amount of data.',
        ],
      },
      {
        heading: 'Data preparation',
        items: [
          'Cleaning, normalization, feature selection.',
          'Physically meaningful features: initial stiffness, apparent elastic modulus, maximum force, maximum stress, strain at failure, absorbed energy, area under the curve.',
        ],
      },
    ],
  },
  {
    id: 'LAB 1',
    title: 'Getting started with experimental data',
    duration: '45 min',
    type: 'lab',
    summary: 'Manipulate a simple dataset inspired by mechanical experiments.',
    items: [
      'Load a .csv file and visualize experimental curves.',
      'Identify noise and outliers.',
      'Extract simple mechanical features and build a feature table for AI methods.',
    ],
    tools: 'MATLAB · Live Scripts (.mlx) · Signal Processing Toolbox',
    paper:
      'Real-world parallel — preparing & fusing multi-sensor data for tool-wear monitoring in milling: Cikala Bagalwa et al., Applied Sciences, 2026 (P4).',
  },
  {
    id: '03',
    title: 'Introduction to Machine Learning',
    duration: '40 min',
    type: 'lecture',
    topics: [
      {
        heading: 'Basic definitions & learning types',
        items: [
          'AI, machine learning, deep learning, model, training, validation, prediction.',
          'Supervised, unsupervised, semi-supervised learning; anomaly detection.',
        ],
      },
      {
        heading: 'General AI workflow',
        items: [
          'Define the mechanical problem → collect data → clean & preprocess → extract features → select model → train → evaluate → interpret physically.',
        ],
      },
    ],
  },
  {
    id: 'LAB 2',
    title: 'Clustering experimental tests',
    duration: '50 min',
    type: 'lab',
    summary: 'Automatically group similar mechanical experiments.',
    items: [
      'Normalize features and apply K-means (optionally hierarchical / DBSCAN).',
      'Choose the number of clusters; visualize clusters in 2D.',
      'Compare clusters with the original experimental curves; discuss their physical meaning.',
    ],
    discussion:
      'Do clusters correspond to distinct mechanical behaviors? Which variables drive the clustering? What happens without normalization? Can clustering detect an abnormal test?',
  },
  {
    id: '04',
    title: 'Supervised Learning for Mechanical Property Prediction',
    duration: '40 min',
    type: 'lecture',
    topics: [
      {
        heading: 'Example problems',
        items: [
          'Predict maximum strength or apparent elastic modulus.',
          'Predict the material class; detect the presence of a defect.',
          'Estimate fatigue life.',
        ],
      },
      {
        heading: 'Models & evaluation',
        items: [
          'Linear regression, decision tree, random forest; SVM and simple neural network as an overview.',
          'Train/test split; MAE, MSE, R²; confusion matrix for classification.',
        ],
      },
    ],
  },
  {
    id: 'LAB 3',
    title: 'Predicting a mechanical property',
    duration: '50 min',
    type: 'lab',
    summary: 'Build a first simple supervised model from extracted features.',
    items: [
      'Split the dataset; train a simple model; evaluate performance.',
      'Plot predicted vs. true values; identify and discuss model errors.',
      'Discuss the physical limitations of the result.',
    ],
  },
  {
    id: '05',
    title: 'Introduction to Deep Learning',
    duration: '35 min',
    type: 'lecture',
    topics: [
      {
        heading: 'Why deep learning & where it helps',
        items: [
          'Complex data: images, time signals, large datasets, automatic feature extraction.',
          'Crack detection from images; classification of fracture surfaces; vibration signal analysis.',
          'Reconstruction of displacement / strain fields; anomaly detection in time series.',
        ],
      },
      {
        heading: 'Useful architectures & limitations',
        items: [
          'Dense networks; CNNs for images; autoencoders for dimensionality reduction and anomaly detection.',
          'LSTM / Transformers for time signals — opening perspective only.',
          'Limitations: data needs, training time, overfitting, interpretability, need for experimental validation.',
        ],
      },
    ],
    paper:
      'Real examples — deep autoencoder for CFRP damage mechanisms (P2); DL on acoustic emission for SLM AlSi10Mg (P3, P5); DL on vibration for bolt loosening (P1).',
  },
  {
    id: 'LAB 4',
    title: 'Mini deep-learning demonstration',
    duration: '35 min',
    type: 'lab',
    summary: 'A guided demonstration rather than a heavy training session.',
    options: [
      'Option A — small dense network on tabular features, compared with a classical model.',
      'Option B — mini image classification on cracks / defects (CNN principle).',
      'Option C — autoencoder trained on normal tests to flag an atypical test.',
    ],
    paper:
      'Each option mirrors a real study — A ← bolt loosening (P1) · B ← AE behavior classification (P5) · C ← CFRP damage autoencoder (P2).',
  },
  {
    id: '06',
    title: 'Opportunities, Limitations & Good Practices',
    duration: '25 min',
    type: 'lecture',
    items: [
      'Opportunities — faster analysis of tests, large-dataset exploration, anomaly detection, reduced post-processing, support for experimental design, coupling AI with physics-based models.',
      'Good practices — always visualize the data, normalize before sensitive models, separate train/test, compare to a simple baseline, relate results to mechanical phenomena.',
      'Limitations — insufficient or biased data, poor data quality, weak generalization, black-box behavior, risk of physically inconsistent conclusions.',
    ],
  },
  {
    id: '07',
    title: 'Conclusion & Discussion',
    duration: '10 min',
    type: 'lecture',
    items: [
      'AI supports classification, prediction, anomaly detection, and data exploration — but data must be carefully prepared and results physically interpreted.',
      "Open discussion: how could these methods apply to the students' own experimental data and projects?",
    ],
  },
];

export const caseStudies = [
  {
    id: 'P1',
    year: '2025',
    journal: 'Meas. Sci. Technol.',
    title:
      'Intelligent monitoring of bolt loosening in industrial customized vehicles using vibration analysis and deep learning',
    authors: 'Carone, Casavola, Katamba Mpoyi, Pappalettera',
    tags: 'Vibration signals · deep learning · SHM',
    usedIn: 'Block 1 · Block 5 · Lab 4 (A)',
  },
  {
    id: 'P2',
    year: '2025',
    journal: 'Applied Sciences',
    title: 'Deep Autoencoder Framework for Classifying Damage Mechanisms in Repaired CFRP',
    authors: 'Barile, Casavola, Katamba Mpoyi, Pappalettera',
    tags: 'Autoencoders · composites · classification',
    usedIn: 'Block 5 · Lab 2 · Lab 4 (C)',
  },
  {
    id: 'P3',
    year: '2024',
    journal: 'Int. J. Damage Mech.',
    title:
      'Enhancing the acoustic emission technique using fuzzy artificial bee colony-based deep learning for SLM AlSi10Mg specimens',
    authors: 'Barile, Casavola, Katamba Mpoyi, Pappalettera, Kannan',
    tags: 'Acoustic emission · DL + optimization · AM',
    usedIn: 'Block 2 · Block 5',
  },
  {
    id: 'P4',
    year: '2026',
    journal: 'Applied Sciences',
    title: 'Cutting Tool Wear Condition Monitoring in Milling Using Deep Learning and Data Fusion',
    authors: "Cikala Bagalwa, Kilundu Y'Ebondo, Katamba Mpoyi*, Casavola, Pappalettera",
    tags: 'Deep learning · multi-sensor data fusion · tool wear',
    usedIn: 'Block 1 · Block 2 · Block 5 · Lab 1 · process assessment',
  },
  {
    id: 'P5',
    year: '2023',
    journal: 'Applied Sciences',
    title:
      'Acoustic Emission and Deep Learning for the Classification of the Mechanical Behavior of AlSi10Mg AM-SLM Specimens',
    authors: 'Barile, Casavola, Pappalettera, Kannan, Mpoyi',
    tags: 'Acoustic emission · deep learning · classification',
    usedIn: 'Block 4 · Block 5 · Lab 4 (B)',
  },
];

export const keyMessage =
  'AI does not replace physical understanding. A good numerical score is not enough — the result must remain mechanically meaningful.';

export type TimingRow = {
  block: string;
  content: string;
  duration: string;
  isLab: boolean;
  isTotal?: boolean;
};

export const timingRows: TimingRow[] = [
  { block: '1', content: 'Introduction and context', duration: '30 min', isLab: false },
  { block: '2', content: 'Experimental data in mechanics', duration: '45 min', isLab: false },
  { block: 'Lab 1', content: 'Getting started with experimental data', duration: '45 min', isLab: true },
  { block: '3', content: 'Introduction to machine learning', duration: '40 min', isLab: false },
  { block: 'Lab 2', content: 'Clustering', duration: '50 min', isLab: true },
  { block: '4', content: 'Supervised learning', duration: '40 min', isLab: false },
  { block: 'Lab 3', content: 'Prediction model', duration: '50 min', isLab: true },
  { block: '5', content: 'Introduction to deep learning', duration: '35 min', isLab: false },
  { block: 'Lab 4', content: 'Mini deep-learning demonstration', duration: '35 min', isLab: true },
  { block: '6', content: 'Opportunities, limitations & good practices', duration: '25 min', isLab: false },
  { block: '7', content: 'Conclusion and discussion', duration: '10 min', isLab: false },
  { block: 'Total', content: 'Lecture + 4 hands-on sessions', duration: '300 min · 5 h', isLab: false, isTotal: true },
];

export const softwareStack = [
  'MATLAB (R2023b or later), worked in Live Scripts (.mlx).',
  'Statistics and Machine Learning Toolbox.',
  'Signal Processing Toolbox.',
  'Deep Learning Toolbox (Lab 4 demonstration).',
  'Interactive apps: Regression Learner, Classification Learner, Deep Network Designer.',
];

export const prerequisites = [
  'Basic programming (MATLAB or any language).',
  'Fundamentals of mechanics and experimental testing.',
  'No prior machine-learning experience required.',
];